import { Permission, UserPermissions, hasAnyPermission } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { DefaultSecondaryButton } from '@frontend/components/common';
import { useModals } from '@frontend/modal';
import { ExtendedNivalizTransaction, ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { DelegateTransactionModal } from '../delegate-transaction-modal/delegate-transaction-modal.component';

/** PuDo and Nivaliz share sender/receiver parties and the same delegate workflow triggers. */
export type DelegatableTransaction = ExtendedPudoTransaction | ExtendedNivalizTransaction;

interface DelegateTransactionActionProps {
    transaction: DelegatableTransaction;
    size?: 'sm' | 'md' | 'lg';
    onSubmitCallback?: () => void;
}

interface CompactHeaderActionItem {
    label: ReactElement;
    onClick: () => void;
}

const NON_DELEGATABLE_STATES: TransactionStateName[] = [
    TransactionStateName.TRANSACTION_COMPLETED,
    TransactionStateName.TRANSACTION_COMPLETED_WITH_FAILURES,
    TransactionStateName.TRANSACTION_CANCELLED,
    TransactionStateName.TRANSACTION_FAILED
];

export const getDelegateTransactionPermissions = (transaction: DelegatableTransaction): Permission[] => [
    { item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.UPDATE, uid: transaction.account_id },
    { item: EntityType.TRANSACTION, source: EntityType.TRANSACTION, right: Rights.UPDATE, uid: transaction.id }
];

export const canDelegateTransaction = (permissions: UserPermissions | null | undefined, transaction: DelegatableTransaction): boolean => {
    if (!permissions) return false;
    if (NON_DELEGATABLE_STATES.includes(transaction.state)) return false;
    return hasAnyPermission(permissions, getDelegateTransactionPermissions(transaction));
};

const createOpenDelegateModalHandler = (
    openModal: ReturnType<typeof useModals>['openModal'],
    transaction: DelegatableTransaction,
    onSubmitCallback?: () => void
) => {
    return () => {
        openModal(
            `delegate-transaction-modal-${transaction.id}`,
            () => (
                <DelegateTransactionModal
                    transaction={transaction}
                    onSubmitCallback={onSubmitCallback}
                />
            ),
            {},
            { className: 'w-[520px]' }
        );
    };
};

export const DelegateTransactionActionButton = (props: DelegateTransactionActionProps) => {
    const { openModal } = useModals();

    return (
        <DefaultSecondaryButton
            text={
                <FormattedMessage
                    id='platform.transaction.delegate-transaction-action.button'
                    description='Delegate button label in the transaction detail modal header'
                    defaultMessage='Delegate'
                />
            }
            onClick={createOpenDelegateModalHandler(openModal, props.transaction, props.onSubmitCallback)}
            size={props.size ?? 'sm'}
        />
    );
};

export const getDelegateTransactionCompactActionItem = (props: {
    transaction: DelegatableTransaction;
    openModal: ReturnType<typeof useModals>['openModal'];
    onSubmitCallback?: () => void;
}): CompactHeaderActionItem => ({
    label: (
        <FormattedMessage
            id='platform.transaction.delegate-transaction-action.button'
            description='Delegate button label in the transaction detail modal header'
            defaultMessage='Delegate'
        />
    ),
    onClick: createOpenDelegateModalHandler(props.openModal, props.transaction, props.onSubmitCallback)
});
